import {CSSProperties, forwardRef, useMemo} from "react";
import {Page, pdfTableStyleNoAlternating} from "../../../../misc/pdf/parts/Page";
import moment from "moment/moment";
import {PriceLine, PriceRider} from "../../../../api/Pricing";
import {first, formatCents} from "nate-react-api-helpers";
import { grey } from "@mui/material/colors";
import {PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {PDF} from "../../../../misc/pdf/parts/PDF";
import {useReadyCheck} from "../../../../misc/pdf/UseReadyCheck";
import {QuoteMiscPrice} from "../../../../api/QuoteMiscPricing";
import {PriceSectionNote} from "../../../../api/QuotePriceSectionNotes";
import {Project, UnitSystem} from "../../../../api/Projects";
import {Company} from "../../../../api/Companies";
import {DescriptionInput, formatDescription} from "../../../logistics/purchasing/toSource/SubmitButton";
import {QuotePriceLine, QuotePriceRider} from "../../../../api/QuotePricing";
import {PDFHeader2} from "../../../../misc/pdf/parts/PDFHeader2";
import {Grid} from "@mui/material";
import {universalParser} from "../../../../misc/scroller/Table";
import {MiscPrice} from "../../../../api/MiscPricing";
import {addHmDoors} from "./quotePdf/HMSection";
import {addWoodDoors} from "./quotePdf/WoodDoorSection";
import {addHardwareSection} from "./quotePdf/HardwareSection";
import {addDiv10Section} from "./quotePdf/Div10Sections";
import {addGlazingSection} from "./quotePdf/GlazingSection";
import {addMiscSection} from "./quotePdf/MiscSection";
import {autoRecombine, QuotePDFBuilder} from "./quotePdf/QuotePDFBuilder";
import {Contact} from "../../../../api/Contacts";
import {hollowMetalDoor, hollowMetalFrame, woodDoor, woodFrame} from "../openings/Const";
import {Product} from "../../../../api/Products";
import {hardwareAlKeyName} from "../../shopdrawing/pricing/PriceSectionNotes";
import {MetricDistance} from "../../../../misc/MetricDistance";
import {ImperialDistance} from "../../../../misc/ImperialDistance";
import {useDdlDataWithoutContext, useDropdownData, ValueItem} from "../../settings/DropDowns";

export type SubCategories = {
    name: string;
    filter: (i: QuotePriceLine) => boolean;
    filterRider: (i: QuotePriceRider) => boolean;
    filterMisc?: (i: QuoteMiscPrice) => boolean;
    children?: (SubCategories | false)[];

    adminExcludeRiders?: boolean;
}

export type Item = {
    type: 'title';
    value: string;
    group: string;
} | {
    type: "section-meta";
    value: string;
    group: string;
    rows: QuotePriceLine[] | PriceLine[];
    riders: QuotePriceRider[]
    misc: MiscPrice[]|QuoteMiscPrice[]
    categories: SubCategories[]
    totalCents: number;
} | {
    type: "row",
    value: QuotePriceLine;
    group: string;
} | {
    type: "footer",
    notes: string[];
    totalCents: number;
    group: string;
} | {
    type: "text",
    notes: string[];
    group: string;
} | {
    type: "total",
    totalCents: number;
    totalCostCents: number;
    group: string | undefined;
} | {
    type: "generalNoteSection",
    value: string
    isFirst: boolean;
    group: string | undefined;
}

export const deepBlue = "#1F3864"

function replaceMap(value: string|null, obj: {[k: string]: string}) {
    if(!value) return value;
    const rpl = obj[value];
    if(rpl) {
        return rpl;
    }

    return value;
}

export function humanizeDoorCore(ddlOptions: ValueItem[], core: string | null | undefined, series: string | undefined | null) {
    let cores = ddlOptions.filter(c => c.category === "doorCore");
    if(series) {
        cores = cores.filter(c => c.doorSeries === series);
    }

    return replaceMap(core || null, {
        "HC": cores.find(c => c?.value === "HC")?.calculatedPrintLabel || "HC",
        "PART": "Solid Core",
        "MIN": "Mineral Core",
        "AG": "AG Fiber",
    })
}

export function toDescriptionInputProduct(input: Product, ddlOptions: ValueItem[]) {
    return toDescriptionInput(Object.assign({}, input, {
        productName: input.catalogs.find(c => true)?.name || input.name,
        productType: input.kind,
        productDimensions: input.dimensions,
    }), {
        ddlOptions,
    })
}

export function toDescriptionInput(input: PriceLine|QuotePriceLine|(Product & {
    productName: string;
    productType: string;
    productDimensions: string;
}), opts: {
    ddlOptions: ValueItem[];
    overrideDoorFrameUnitSystem?: UnitSystem
}): DescriptionInput {

    let material = input.door?.material || null
    if(material && input.door?.series === woodDoor) {
        if(material === "Hardboard") {
            // do nothing
        } else {
            material = material + " Veneer";
        }
    } else if(input.door?.series === hollowMetalDoor) {
        material = "";
    }

    return {
        categoryName: input.categoryName || null,
        doorCore: humanizeDoorCore(opts.ddlOptions, input.door?.core || null, input.door?.series),
        doorLabel: input.door?.label || null,
        doorElevation: input.door?.elevation || null,
        doorThickness: input.door?.thickness || null,
        doorMaterial: material,
        doorSeries: input.door?.series || null,
        doorGauge: input.door?.gauge || null,
        doorSeam: input.door?.seam || null,
        doorFinish: input.door?.finish || null,
        doorPreps: input.door?.preps as any || null,

        frameSeries: input.frame?.series || null,
        frameProfile: input.frame?.profile || null,
        frameConstruction:  input.frame?.construction || null,
        frameType: input.frame?.type || null,
        frameMaterial: input.frame?.material || null,
        frameHanding: input.frame?.handing || null,
        frameLabel: input.frame?.label || null,
        frameGauge: input.frame?.gauge || null,
        frameScreenElev: input.frame?.screenElev || null,
        frameJambDepth: input.frame?.jambDepth || null,
        frameDoorThickness: input.frame?.doorThickness || null,
        framePreps: input.frame?.preps as any || null,

        div10Category: input.div10?.category || null,
        div10LockerTiers: input.div10?.lockerTiers || null,
        div10LockerCoating: input.div10?.lockerCoating || null,
        div10LockerSloped: input.div10?.lockerSloped || null,
        div10LockerEndPanels: input.div10?.lockerEndPanels || null,
        div10MailboxLoading: input.div10?.mailboxLoading || null,
        div10MailboxMounting: input.div10?.mailboxMounting || null,
        div10MailboxTenantUnits: input.div10?.mailboxTenantUnits || null,
        div10MailboxParcelUnits: input.div10?.mailboxParcelUnits || null,
        div10PartitionBracing: input.div10?.partitionBracing || null,
        div10PartitionCoating: input.div10?.partitionCoating || null,
        div10Description: input.div10?.description || null,
        div10Note: input.div10?.note || null,

        description: input.productType === "frame-anchor" ? "Anchor: " + input.productName : input.productName,
        productCode: input.productCode,
        finish: input.finish,
        dimensions: input.productDimensions,
        dimWidth: autoAdjustDimension({
            value: input.dimWidth || input.frame?.width || input.door?.width || "",
            toUnitSystem: input.productType === "door" || input.productType === "frame" ? opts?.overrideDoorFrameUnitSystem : undefined,
        }),
        dimHeight: autoAdjustDimension({
            value: input.dimHeight || input.frame?.height || input.door?.height || "",
            toUnitSystem: input.productType === "door" || input.productType === "frame" ? opts?.overrideDoorFrameUnitSystem : undefined,
        }),
        dimLength: input.dimLength,
    }
}

function autoAdjustDimension(input: {
    value: string;
    toUnitSystem?: UnitSystem;
}) {
    if(!input.toUnitSystem) return input.value;
    const uu = universalParser(input.value);

    if(input.toUnitSystem === "metric") {
        return MetricDistance.fromUniversal(uu).toString();
    }

    return ImperialDistance.fromUniversal(uu).toString();
}

export type TQuotePage = "hollow-metal" | "doors" | "wood-doors" | "glazing" | "hardware" | "div10" | "wr-accessories" | "wr-partitions" | "lockers" | "mailboxes" | "corner-guards" | "misc" | "hardware/split-AL";

export type QuotePDFData = {
    prices: QuotePriceLine[]
    riders: QuotePriceRider[];
    miscPrices: (QuoteMiscPrice[]|MiscPrice[])
    notes: PriceSectionNote[];
    project: Project;
    client: Company;
    clientContact?: Contact;
    currentQuoteNumber: string;
    pages?: TQuotePage[];
}

export function fixShortForms(value: string): string {
    const list = [
        {search: /( |^)(KD)( |$)/g, value: "Knockdown"},
        {search: /( |^)(WELD)( |$)/g, value: "Welded"},
        {search: /( |^)(HC)( |$)/g, value: "Honeycomb"},
        {search: /( |^)(PS)( |$)/g, value: "Insulated"},
    ]

    list.map(v => {
        value = value.replace(v.search, (match: string, p1: string, p2: string, p3: string) => {
            return p1 + v.value + p3;
        });
    })

    return value;
}

export const subHeaderCss: CSSProperties = {
    fontWeight: "bold",
    color: deepBlue,
    textTransform: 'uppercase',
    fontSize: 12,
    borderBottom: "1px solid " + grey["500"],
    paddingBottom: 2,
    marginBottom: 2,
}

export function useData(props: {
    prices: QuotePriceLine[];
    notes: PriceSectionNote[];
    miscPrices: (QuoteMiscPrice[]|MiscPrice[]);
    riders: (QuotePriceRider[])|(PriceRider[]);
    pages?: TQuotePage[]
    excludeRiders?: boolean;
}) {
    return useMemo(() => {
        return getDataInner({
            prices: props.prices,
            notes: props.notes,
            miscPrices: props.miscPrices,
            riders: props.riders,
            pages: props.pages,
            excludeRiders: props.excludeRiders,
        });
    }, [props.prices, props.notes, props.miscPrices, props.riders, props.pages, props.excludeRiders])
}

export function getDataInner(props: {
    prices: (PriceLine[])|(QuotePriceLine[])
    notes: PriceSectionNote[];
    miscPrices: (QuoteMiscPrice[]|MiscPrice[]);
    riders: QuotePriceRider[];
    pages?: TQuotePage[]
    excludeRiders?: boolean;
}) {
    const bld = new QuotePDFBuilder();
    bld.pages = props.pages;
    bld.excludeRiders = !!props.excludeRiders;

    const prices = props.prices as QuotePriceLine[];
    const miscPrices = props.miscPrices;
    const notes = props.notes.filter(v => !!v.note);

    if(props.pages === undefined) {
        // default pages in default order
        props.pages = ["hollow-metal", "wood-doors", "glazing", "hardware", "wr-accessories", "wr-partitions", "lockers", "mailboxes", "corner-guards", "misc"];
    }

    // use pages order to add sections to PDF
    props.pages.map(p => {
        switch(p) {
            case "hollow-metal":
                addHmDoors(bld, {
                    riders: props.riders,
                    prices: prices,
                    notes: notes,
                });
                break;
            case "wood-doors":
                addWoodDoors(bld, {
                    prices: prices,
                    riders: props.riders,
                    notes: notes,
                });
                break;
            case "hardware":
                let hwPrices = prices;
                let title = "Finishing Hardware";

                if(props.pages?.includes("hardware/split-AL")) {
                    hwPrices = prices.map(hardwareAluminumTransform(false)).filter(v => v.extendedPrice > 0)
                    title = "Finishing Hardware"
                }

                addHardwareSection(bld, {
                    title: title,
                    prices: hwPrices,
                    riders: props.riders,
                    notes: notes.filter(n => n.keyName === "hardware"),
                })
                break;
            case "hardware/split-AL":
                addHardwareSection(bld, {
                    title: "Aluminum Hardware",
                    prices: prices.map(hardwareAluminumTransform(true)).filter(v => v.extendedPrice > 0),
                    riders: [],
                    notes: notes.filter(n => n.keyName === hardwareAlKeyName),
                })
                break;
            case "glazing":
                addGlazingSection(bld, {
                    prices: prices,
                    riders: props.riders,
                    notes: notes,
                });
                break;
            case "wr-accessories":
                addDiv10Section(bld, {
                    prices: prices,
                    riders: props.riders,
                    notes: notes,
                    div10Key: "wr-accessory",
                });
                break;
            case "wr-partitions":
                addDiv10Section(bld, {
                    prices: prices,
                    riders: props.riders,
                    notes: notes,
                    div10Key: "wr-partition",
                });
                break;
            case "lockers":
                addDiv10Section(bld, {
                    prices: prices,
                    riders: props.riders,
                    notes: notes,
                    div10Key: "locker",
                });
                break;
            case "mailboxes":
                addDiv10Section(bld, {
                    prices: prices,
                    riders: props.riders,
                    notes: notes,
                    div10Key: "mailbox",
                });
                break;
            case "corner-guards":
                addDiv10Section(bld, {
                    prices: prices,
                    riders: props.riders,
                    notes: notes,
                    div10Key: "corner-guard",
                });
                break;
            case "misc":
                addMiscSection(bld, {
                    miscPrices: miscPrices,
                });
                break;
        }
    })

    if(props.pages === undefined) {
        const skippedRiders = props.riders.filter(v => !bld.ridersTouched.includes(v));
        if (skippedRiders.length > 0) {
            throw new Error("Skipped riders: " + skippedRiders.map(v => v.sectionKey).join(", "));
        }
    }

    bld.completeCategoryBuilding();
    bld.addTotal();
    bld.addGeneralNotes(notes);

    return bld.compile();
}

function hardwareAluminumTransform(forAluminum: boolean) {
    return (p: QuotePriceLine) => {
        let alQty = 0;
        if(p.openingMeta) {
            alQty = p.openingMeta.reduce((acc, item) => {
                if([woodDoor, hollowMetalDoor].includes(item.doorSeries)) return acc;
                if([woodFrame, hollowMetalFrame].includes(item.frameSeries)) return acc;

                return acc + item.qty;
            }, 0);
        }

        if(forAluminum) {
            return Object.assign({}, p, {
                qty: alQty,
                extendedPrice: calcExt({
                    unitCostCents: p.unitCostCents,
                    qty: alQty,
                    markupPercent: p.markupPercent,
                    markupCents: p.markupCents,
                }),
            })
        } else {
            const qty = p.qty - alQty;

            return Object.assign({}, p, {
                qty: qty,
                // use negative to give better accuracy (rounding)
                extendedPrice: p.extendedPrice - calcExt({
                    unitCostCents: p.unitCostCents,
                    qty: alQty,
                    markupPercent: p.markupPercent,
                    markupCents: p.markupCents,
                }),
            })
        }
    }
}

function calcExt(input: {
    unitCostCents: number;
    qty: number;
    markupPercent?: number;
    markupCents?: number;
}) {
    if(input.markupPercent !== undefined) {
        return Math.round(input.unitCostCents * input.qty * (1 + input.markupPercent / 100));
    }

    if(input.markupCents !== undefined) {
        return Math.round((input.unitCostCents + input.markupCents) * input.qty);
    }

    return Math.round(input.unitCostCents * input.qty);
}

export const QuotePDF = forwardRef(function(props: {
    project: number;
    data: QuotePDFData;
    onReady(): void;
}, ref: any) {
    const data = useData({
        prices: props.data.prices,
        miscPrices: props.data.miscPrices,
        notes: props.data.notes,
        riders: props.data.riders,
        pages: props.data.pages,
    });

    const ddlCtx = useDdlDataWithoutContext(); // PDFs are rendered outside of the typical context
    const ddlOptions = useDropdownData(ddlCtx.root);

    const ready = useReadyCheck(props.onReady);
    ready.require(!ddlCtx.loading, "drop-down-data");

    return (
        <div ref={ref}>
            <PDF>
                <PageRenderProvider<Item> data={data} onReady={ready.section()}>
                    {(info)=> {

                        const hasTableRows = !!info.rows.find(v => v.type === "row" || v.type === "title" || v.type === "text" || v.type === "footer" || v.type === "total")

                        return (<Page orientation="portrait">
                            <div style={{flex: 1, fontSize: 14}}>
                                {info.pageIndex === 0 && <>
                                    <PDFHeader2 title="Quotation" sales />

                                    <Grid container spacing={3}>
                                        <Grid item xs={4}>
                                            <div>
                                                <div style={subHeaderCss}>Quote To:</div>
                                                <div>{props.data.client.name}</div>
                                                {props.data.client.addressStreet && <>
                                                    <div>{props.data.client.addressStreet}</div>
                                                    <div>{props.data.client.addressCity}, {props.data.client.addressProvince}</div>
                                                    <div>{props.data.client.addressPostal}</div>
                                                </>}
                                            </div>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <div>
                                                <div style={subHeaderCss}>Ship To:</div>
                                                <div>{props.data.project.name}</div>
                                                {props.data.project && <>
                                                    <div>{props.data.project.addressStreet}</div>
                                                    <div>{props.data.project.addressCity}, {props.data.project.addressProvince}</div>
                                                    <div>{props.data.project.addressPostal}</div>
                                                </>}
                                            </div>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <table>
                                                <tbody>
                                                <tr>
                                                    <td style={{fontWeight: "bold", textAlign: 'right'}}>Quote No:</td>
                                                    <td>#{props.data.currentQuoteNumber}</td>
                                                </tr><tr>
                                                    <td style={{fontWeight: "bold", textAlign: 'right'}}>Date:</td>
                                                    <td>{moment().format("MMMM D, YYYY")}</td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </Grid>
                                    </Grid>

                                    <div style={{height: 16}} />
                                </>}

                                {hasTableRows && <table className={pdfTableStyleNoAlternating} style={{fontSize: 12}}>
                                    <thead>
                                    <tr>
                                        <th style={{borderRight: "none"}}>Description</th>
                                        <th style={{borderLeft: "none"}}>Total</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {autoRecombine(info.rows).map((i, index) => {
                                        switch(i.type) {
                                            case "title":
                                                return (
                                                    <tr key={index.toString()} style={{borderBottom: "none", borderTop: "1px solid"}}>
                                                        <td colSpan={2} style={{fontWeight: "bold"}}>
                                                            {i.value}
                                                        </td>
                                                    </tr>
                                                )
                                            case "row":
                                                if(i.value.productType === "hardware") return null;
                                                if(i.value.productType === "frame-anchor") return null;

                                                return (
                                                    <tr key={index.toString()}>
                                                        <td style={{borderRight: "none"}}>{i.value.qty} @ {fixShortForms(formatDescription(toDescriptionInput(i.value, {
                                                            overrideDoorFrameUnitSystem: props.data.project.unitSystem,
                                                            ddlOptions,
                                                        }), {
                                                            ddlOptions,
                                                            plural: i.value.qty > 1,
                                                            forQuote: true,
                                                        }))}</td>
                                                        <td style={{textAlign: "right", borderLeft: "none"}}></td>
                                                    </tr>
                                                )
                                            case "text":
                                                return (
                                                    <tr key={index.toString()} style={{borderBottom: "1px solid"}}>
                                                        <td style={{borderRight: "none"}}>
                                                            {i.notes.map(n => <div style={{whiteSpace: "pre-wrap"}}>{n}</div>)}
                                                        </td>
                                                        <td style={{textAlign: "right", verticalAlign: "bottom", borderLeft: "none"}}>
                                                        </td>
                                                    </tr>
                                                )
                                            case "footer":
                                                return (
                                                    <tr key={index.toString()} style={{borderBottom: "1px solid"}}>
                                                        <td style={{borderRight: "none"}}>
                                                            {i.notes.map(n => <div style={{whiteSpace: "pre-wrap"}}>{n}</div>)}
                                                        </td>
                                                        <td style={{textAlign: "right", verticalAlign: "bottom", borderLeft: "none"}}>
                                                            ${formatCents(i.totalCents)}
                                                        </td>
                                                    </tr>
                                                )
                                            case "total":
                                                return (
                                                    <tr key={index.toString()} style={{border: "none"}}>
                                                        <td style={{fontWeight: "bold", textAlign: "right", color: deepBlue, border: "none"}}>TOTAL</td>
                                                        <td style={{textAlign: "right", fontWeight: "bold", border: "none", paddingLeft: 0, paddingTop: 0}}>
                                                            <div style={{display: "flex", justifyContent: "flex-end"}}>
                                                                <div>${formatCents(i.totalCents)}</div>
                                                                <div style={{fontSize: "0.8em", paddingLeft: 4, paddingBottom: 1, alignSelf: "flex-end"}}>+HST</div>
                                                            </div>
                                                            <div style={{borderBottom: "1px solid black", borderTop: "1px solid black", width: "100%", height: 4}} />
                                                        </td>
                                                    </tr>
                                                )
                                        }
                                    })}
                                    </tbody>
                                </table>}

                                {first(info.rows, i => i.type === "total") &&
                                    <div>
                                        <div style={{marginTop: 30,
                                            display: "flex", flexDirection: "column", alignItems: "center", width: 300}}>
                                            <div>Thank you for your business!</div>

                                            <div style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                fontSize: "0.7em",
                                                fontWeight: "600",
                                                color: deepBlue,
                                            }}>
                                                <div>Tykel Commercial Door Supply Inc</div>
                                                <div>HST #713417319RT0001</div>
                                            </div>

                                        </div>
                                        <div style={{color: deepBlue, textAlign: "center", paddingTop: 10, fontWeight: "600"}}>
                                            **ALL QUOTED ITEMS MUST BE ORDERED WITHIN 90 DAYS OF QUOTE DATE**
                                        </div>

                                    </div>}

                                {info.rows.map(r => {
                                    if(r.type === "generalNoteSection") {
                                        const onSamePageAsTable = !!info.rows.find(v => v.type === "total")
                                        return (
                                            <>
                                                {r.isFirst && <div style={{marginTop: onSamePageAsTable ? 30 : 8, fontWeight: "700"}}>General Notes</div>}
                                                <p style={{whiteSpace: "pre-wrap", lineHeight: "1.4em", margin: 0}}>
                                                    {r.value || <>&nbsp;</>}
                                                </p>
                                            </>
                                        )
                                    }
                                })}
                            </div>
                        </Page>)
                    }}
                </PageRenderProvider>
            </PDF>
        </div>
    )
});
